import { FunctionComponent, useEffect, useState } from 'react';
import { Box, CircularProgress, FormHelperText, Stack, TextField, Typography } from '@mui/material';
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper';
import MfaService from '../../../../shared/services/mfa/mfa.service';
import { ITotpSetupResponse } from '../../../../shared/services/mfa/mfa.model';

// Authenticator enrolment: fetch a fresh secret + QR on open, then confirm with a
// code from the app. On success the parent receives the one-time backup codes.
export const TotpSetupDialog: FunctionComponent<{
  open: boolean,
  onClose: () => void,
  onConfirmed: (backupCodes: string[]) => void,
}> = ({ open, onClose, onConfirmed }) => {
  const mfaService = new MfaService();
  const [setup, setSetup] = useState<ITotpSetupResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!open) return;
    setSetup(null);
    setLoading(true);
    setLoadError('');
    setCode('');
    setErrorMessage('');
    mfaService
      .setupTotp()
      .then((response) => {
        setSetup(response);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setLoadError(err?.response?.data?.message || 'We could not start setup. Please try again.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = async () => {
    if (code.length !== 6 || submitting) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const response = await mfaService.confirmTotp(code);
      setSubmitting(false);
      onConfirmed(response.backupCodes);
    } catch (err: any) {
      setSubmitting(false);
      setErrorMessage(err?.response?.data?.message || 'That code is not right. Check the app and try again.');
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title="Set up authenticator app"
      cancelHandler={onClose}
      submitHandler={handleConfirm}
      isSubmitting={submitting}
      disableSaveButton={loading || !!loadError || code.length !== 6}
      submitButtonText="Verify and turn on"
      maxWidth="sm"
    >
      {loading && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {!loading && loadError && (
        <FormHelperText error sx={{ fontSize: '0.875rem' }}>{loadError}</FormHelperText>
      )}

      {!loading && setup && (
        <Stack spacing={2}>
          <Typography variant="body1">
            Scan this QR code with an authenticator app (for example Google Authenticator, Microsoft Authenticator, Authy or 1Password).
          </Typography>
          <Box sx={{ alignSelf: 'center', width: 200, height: 200 }}>
            <img src={setup.qrDataUri} alt="Authenticator QR code" style={{ width: '100%', height: '100%' }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              Can&apos;t scan it? Enter this key manually:
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '1rem', letterSpacing: '0.08em', wordBreak: 'break-all' }}>
              {setup.secret}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Enter the 6-digit code from the app to finish.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
              inputProps={{ inputMode: 'numeric', maxLength: 6, 'aria-label': 'Authenticator code' }}
            />
            {errorMessage && (
              <FormHelperText error sx={{ mt: 1, fontSize: '0.8125rem' }}>{errorMessage}</FormHelperText>
            )}
          </Box>
        </Stack>
      )}
    </DialogWrapper>
  );
};
